<!-- Admin Tree Navigation Component -->
<div class="admin-tree-container" [class.collapsed]="isCollapsed" [class.edit-mode-active]="isEditMode">
  
  <!-- Tree Header -->
  <mat-toolbar class="tree-header">
    <span class="tree-title">Admin Operations</span>
    <span class="spacer"></span>
    
    <!-- Tree Actions -->
    <div class="tree-actions" *ngIf="!isCollapsed">
      <!-- Expand/Collapse All -->
      <button mat-icon-button 
              (click)="toggleAllNodes()" 
              [matTooltip]="isTreeExpanded ? 'Collapse All' : 'Expand All'"
              class="action-button">
        <mat-icon>{{ isTreeExpanded ? 'unfold_less' : 'unfold_more' }}</mat-icon>
      </button>
      
      <!-- Refresh Tree -->
      <button mat-icon-button 
              (click)="refreshTree()" 
              matTooltip="Refresh Tree"
              class="action-button"
              [disabled]="isLoading">
        <mat-icon [class.spinning]="isLoading">refresh</mat-icon>
      </button>
      
      <!-- Edit Mode Toggle -->
      <mat-slide-toggle
        class="edit-toggle"
        [checked]="isEditMode"
        (change)="toggleEditMode($event)"
        color="primary"
        matTooltip="Toggle Edit Mode">
        Edit
      </mat-slide-toggle>
    </div>
  </mat-toolbar>

  <!-- Loading Indicator -->
  <div *ngIf="isLoading" class="loading-container">
    <mat-progress-spinner diameter="24" mode="indeterminate" color="primary"></mat-progress-spinner>
    <span>Loading admin tree...</span>
  </div>

  <!-- Expanded Tree Content -->
  <div class="tree-content" *ngIf="!isCollapsed && !isLoading">
    
    <!-- No Data State -->
    <div *ngIf="dataSource.data.length === 0" class="no-data-container">
      <mat-icon class="no-data-icon">warning</mat-icon>
      <p>No admin tree data available</p>
      <button mat-raised-button color="primary" (click)="refreshTree()">
        <mat-icon>refresh</mat-icon>
        Retry
      </button>
    </div>

    <!-- Tree Data -->
    <mat-tree *ngIf="dataSource.data.length > 0" [dataSource]="dataSource" [treeControl]="treeControl">
      
      <!-- Leaf Node Template (Actions) -->
      <mat-tree-node *matTreeNodeDef="let node" 
                     matTreeNodePadding
                     [matTreeNodePaddingIndent]="24"
                     class="tree-node action-node"
                     [class.selected]="isNodeSelected(node)">
        
        <div class="node-content">
          <!-- Node Icon and Name -->
          <div class="node-info" (click)="onNodeSelect(node)">
            <mat-icon class="node-icon">{{ getNodeIcon(node) }}</mat-icon>
            <span class="node-name" [matTooltip]="getNodeTooltip(node)">{{ node.name }}</span>
          </div>
          
          <!-- Edit Controls -->
          <div *ngIf="canEditDeleteNode(node)" class="edit-controls">
            <button mat-icon-button 
                    class="edit-button"
                    matTooltip="Rename node"
                    (click)="editNode(node); $event.stopPropagation()">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button 
                    class="delete-button"
                    matTooltip="Delete node"
                    (click)="deleteNode(node); $event.stopPropagation()">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>
      </mat-tree-node>

      <!-- Parent Node Template (Operations) -->
      <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild" 
                            class="tree-node operation-node"
                            [class.selected]="isNodeSelected(node)">
        
        <div class="mat-tree-node">
          <div class="node-content">
            <!-- Node Icon and Name -->
            <div class="node-info" (click)="onNodeSelect(node)">
              <mat-icon class="node-icon">{{ getNodeIcon(node) }}</mat-icon>
              <span class="node-name" [matTooltip]="getNodeTooltip(node)">{{ node.name }}</span>
            </div>
            
            <!-- Edit Controls -->
            <div class="edit-controls">
              <button *ngIf="canAddNode(node)" 
                      mat-icon-button 
                      class="add-button"
                      matTooltip="Add child node"
                      (click)="addNode(node); $event.stopPropagation()">
                <mat-icon>add_circle</mat-icon>
              </button>
              <button *ngIf="canEditDeleteNode(node)" 
                      mat-icon-button 
                      class="edit-button"
                      matTooltip="Rename node"
                      (click)="editNode(node); $event.stopPropagation()">
                <mat-icon>edit</mat-icon>
              </button>
              <button *ngIf="canEditDeleteNode(node)" 
                      mat-icon-button 
                      class="delete-button"
                      matTooltip="Delete node"
                      (click)="deleteNode(node); $event.stopPropagation()">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
            
            <!-- Expand/Collapse Toggle -->
            <button mat-icon-button 
                    matTreeNodeToggle
                    [attr.aria-label]="'Toggle ' + node.name"
                    class="toggle-button"
                    (click)="$event.stopPropagation()">
              <mat-icon>
                {{ treeControl.isExpanded(node) ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            </button>
          </div>
        </div>

        <!-- Children Container -->
        <div [class.tree-invisible]="!treeControl.isExpanded(node)" 
             role="group" 
             class="children-container">
          <ng-container matTreeNodeOutlet></ng-container>
        </div>
      </mat-nested-tree-node>
    </mat-tree>
  </div>

  <!-- Collapsed State View - Similar to app-home navigation -->
  <div *ngIf="isCollapsed" class="collapsed-navigation">
    <!-- Expand button at the top -->
    <button mat-icon-button
            class="expand-button"
            (click)="toggleCollapse()"
            matTooltip="Expand Admin Tree"
            matTooltipPosition="right">
      <mat-icon>menu</mat-icon>
    </button>

    <!-- Mode indicator -->
    <div class="nav-mode-indicator">
      <mat-icon>admin_panel_settings</mat-icon>
      <span class="mode-label">ADMIN</span>
    </div>

    <!-- Operations quick access -->
    <div class="collapsed-items-container scrollable-container" *ngIf="!isLoading">
      <div *ngIf="getOperationNodes().length === 0" class="loading-indicator">
        <span>No operations</span>
      </div>

      <div *ngFor="let operation of getOperationNodes(); let i = index"
           class="collapsed-item"
           [class.selected]="isNodeSelected(operation)"
           (click)="onNodeSelect(operation)"
           [matTooltip]="operation.name"
           matTooltipPosition="right">
        <span class="collapsed-label">{{ getCollapsedLabel(operation) }}</span>
      </div>
    </div>

    <!-- Loading in collapsed state -->
    <div *ngIf="isLoading" class="loading-indicator">
      <mat-progress-spinner diameter="20" mode="indeterminate" color="primary"></mat-progress-spinner>
    </div>
  </div>
</div> 